import { convertToIsoDateTime } from '@pkg/utils';

import { apiFactory } from '../../apiFactory/api';
import { apiReturnPaginatedResults, apiReturnResults } from '../../apiFactory/apiReturn';
import {
    type ApiResult,
    type BaseParams,
    type CollectionChainType,
    type ContinuationResult,
    type DaasCollectionTokens,
    Request,
} from '../../common';
import { links } from '../../links';

import {
    type CollectionFloorPriceItemResponse,
    type CollectionFloorPriceResponse,
    type CollectionListingsCount,
    type CollectionListingsCountResponse,
    type CollectionListingsInRangeResponse,
    type CollectionListingsItem,
    type CollectionListingsResponse,
    type CollectionMarketplaceSale,
    type CollectionMarketplaceSaleResponse,
    type CollectionPastActivityResponse,
    type CollectionResponse,
    type CollectionSalesByDateCount,
    type CollectionSalesByDateCountResponse,
    type CollectionSalesByPriceRangeCount,
    type CollectionSalesByPriceRangeCountResponse,
    type CollectionStatsResponse,
    type CollectionTotalListingsResponse,
    type CollectionTotalOwnersResponse,
    type CollectionVolumeResponse,
    type OwnersActivityListingsResponse,
    type OwnersActivitySalesResponse,
} from './types';

export async function getCollectionMetadataDetails(
    params: BaseParams & { slug: string },
): Promise<ApiResult<CollectionResponse>> {
    const { slug } = params;
    const target = links.collection().metadata;

    return Request.getRequest<CollectionResponse>({
        ...params,
        target,
        query: {
            ...params.query,
            slug,
            w: 1200, // For wider banner image
        },
    });
}

export const callGetCollectionMetadataDetails = (params: BaseParams & { slug: string }): Promise<CollectionResponse> =>
    apiFactory()
        .call(getCollectionMetadataDetails, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting collection ${params.slug}`;
            throw new Error(errorMessage);
        });

export async function getCollectionTokens(
    params: BaseParams & { chain: CollectionChainType },
): Promise<ApiResult<DaasCollectionTokens>> {
    const target = links.collection().chainData(params.chain).tokens;
    return Request.getRequest<DaasCollectionTokens>({
        ...params,
        target,
    });
}

export const callGetCollectionTokens = (params: BaseParams & { chain: CollectionChainType }) =>
    apiReturnPaginatedResults<DaasCollectionTokens, ContinuationResult<DaasCollectionTokens>>(
        getCollectionTokens,
        params,
    );

export async function getCollectionListings(
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number; limit: number },
): Promise<ApiResult<CollectionListingsResponse>> {
    return Request.getRequest<CollectionListingsResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .listings()
            .byPriceRange(params.minPriceWei, params.maxPriceWei)
            .active(params.limit),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callGetCollectionListings = (
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number; limit: number },
): Promise<CollectionListingsItem[]> =>
    apiFactory()
        .call(getCollectionListings, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.active_ethereum_listings;
            }
            const errorMessage = result.error.detail ?? `Error getting collection listings`;
            throw new Error(errorMessage);
        });

export async function countCollectionFloorGameListings(
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number },
): Promise<ApiResult<CollectionListingsCountResponse>> {
    return Request.getRequest<CollectionListingsCountResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .listings()
            .byPriceRange(params.minPriceWei, params.maxPriceWei)
            .total(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callCountCollectionListings = (
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number },
): Promise<CollectionListingsCount> =>
    apiFactory()
        .call(countCollectionFloorGameListings, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.active_ethereum_listings_aggregate.aggregate.count;
            }
            const errorMessage = result.error.detail ?? `Error getting collection listings count`;
            throw new Error(errorMessage);
        });

export async function countCollectionSalesByPriceRange(
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number },
): Promise<ApiResult<CollectionSalesByPriceRangeCountResponse>> {
    return Request.getRequest<CollectionSalesByPriceRangeCountResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .sales()
            .byPriceRange(params.minPriceWei, params.maxPriceWei)
            .total(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callCountCollectionSalesByPriceRange = (
    params: BaseParams & { contractAddress: string; minPriceWei: number; maxPriceWei: number },
): Promise<CollectionSalesByPriceRangeCount> =>
    apiFactory()
        .call(countCollectionSalesByPriceRange, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.marketplace_sale_aggregate.aggregate.count;
            }
            const errorMessage = result.error.detail ?? `Error getting collection sales count`;
            throw new Error(errorMessage);
        });

export async function countCollectionSalesByDate(
    params: BaseParams & { contractAddress: string; dateFrom: Date },
): Promise<ApiResult<CollectionSalesByDateCountResponse>> {
    const isoDateTime = convertToIsoDateTime(params.dateFrom);
    return Request.getRequest<CollectionSalesByDateCountResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .sales()
            .byDate(isoDateTime)
            .total(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callCountCollectionSalesByDate = (
    params: BaseParams & { contractAddress: string; dateFrom: Date },
): Promise<CollectionSalesByDateCount> =>
    apiFactory()
        .call(countCollectionSalesByDate, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.marketplace_sale_aggregate.aggregate.count;
            }
            const errorMessage = result.error.detail ?? `Error getting collection sales count`;
            throw new Error(errorMessage);
        });

export async function getCollectionSalesByPriceAndDate(
    params: BaseParams & { contractAddress: string; dateFrom: Date; floorPriceWei: number },
): Promise<ApiResult<CollectionMarketplaceSaleResponse>> {
    const isoDateTime = convertToIsoDateTime(params.dateFrom);
    return Request.getRequest<CollectionMarketplaceSaleResponse>({
        ...params,
        target: links
            .daas()
            .graphql()
            .collection()
            .byContract(params.contractAddress)
            .sales()
            .recentTierSales(isoDateTime, params.floorPriceWei),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callGetCollectionByPriceAndDate = (
    params: BaseParams & { contractAddress: string; dateFrom: Date; floorPriceWei: number },
): Promise<CollectionMarketplaceSale[]> =>
    apiFactory()
        .call(getCollectionSalesByPriceAndDate, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value.marketplace_sale;
            }
            const errorMessage = result.error.detail ?? `Error getting collection sales`;
            throw new Error(errorMessage);
        });

export async function getCollectionPastActivity(
    params: BaseParams & { contractAddress: string[] },
): Promise<ApiResult<CollectionPastActivityResponse>> {
    return Request.postRequest<CollectionPastActivityResponse>({
        ...params,
        target: links.daas().graphql().activity().collection(),
        value: { collectionAddresses: params.contractAddress },
        apiSettingsType: 'daasGraphQL',
    });
}

export const callGetCollectionPastActivity = (
    params: BaseParams & { contractAddress: string[] },
): Promise<CollectionPastActivityResponse> =>
    apiFactory()
        .call(getCollectionPastActivity, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting collection past activity`;
            throw new Error(errorMessage);
        });

export async function getCollectionListingsInRange(
    params: BaseParams & { contracts: string[]; minPriceWei: number; maxPriceWei: number; limit: number },
): Promise<ApiResult<CollectionListingsInRangeResponse>> {
    const { contracts, limit, maxPriceWei, minPriceWei, settings, ...otherParams } = params;
    return Request.postRequest<CollectionListingsInRangeResponse>({
        ...otherParams,
        value: { contracts, limit, maxPriceWei, minPriceWei },
        target: links.daas().graphql().collectionActiveListings(),
        apiSettingsType: 'daasGraphQL',
    });
}
export const callGetCollectionListingsInRange = (
    params: BaseParams & { contracts: string[]; minPriceWei: number; maxPriceWei: number; limit: number },
): Promise<CollectionListingsInRangeResponse> =>
    apiFactory()
        .call(getCollectionListingsInRange, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting collection listings`;
            throw new Error(errorMessage);
        });

export async function getCollectionFloorPriceBySlug(
    params: BaseParams & { slug: string },
): Promise<ApiResult<CollectionFloorPriceResponse>> {
    return Request.getRequest<CollectionFloorPriceResponse>({
        ...params,
        target: links.daas().graphql().collection().bySlug(params.slug).floorPrice(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callGetCollectionFloorPriceBySlug = (params: BaseParams & { slug: string }) =>
    apiReturnResults<CollectionFloorPriceItemResponse>(getCollectionFloorPriceBySlug, params);

export const getOwnersActivitySales = (
    params: BaseParams & { slug: string },
): Promise<ApiResult<OwnersActivitySalesResponse>> => {
    const { slug } = params;
    const target = links.daas().marketplace().collections().activity(slug).sales;

    return Request.getRequest<OwnersActivitySalesResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetOwnersActivitySales = (params): Promise<OwnersActivitySalesResponse> => {
    return apiFactory()
        .call(getOwnersActivitySales, { ...params }, { publicRequest: true })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error getting collection floor game listings`;
            throw new Error(errorMessage);
        });
};

export const getOwnersActivityListings = (
    params: BaseParams & { slug: string },
): Promise<ApiResult<OwnersActivityListingsResponse>> => {
    const { slug } = params;
    const target = links.daas().marketplace().collections().activity(slug).listings;

    return Request.getRequest<OwnersActivityListingsResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetOwnersActivityListings = (params: BaseParams & { slug?: string }) =>
    apiReturnResults<OwnersActivityListingsResponse>(getOwnersActivityListings, params, true);

export async function getCollectionTotalSalesVolume(
    params: BaseParams & { slug: string; days: number },
): Promise<ApiResult<CollectionVolumeResponse>> {
    const { slug, days } = params;
    const target = links.collection().totalSalesVolumeInDays(slug);
    return Request.getRequest<CollectionVolumeResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
        query: {
            ...params.query,
            days,
        },
    });
}

export const callGetCollectionTotalSalesVolume = (params: BaseParams & { slug: string; days: number }) =>
    apiReturnResults<CollectionVolumeResponse>(getCollectionTotalSalesVolume, params, true);

export async function getCollectionTotalListings(
    params: BaseParams & { slug: string },
): Promise<ApiResult<CollectionTotalListingsResponse>> {
    const { slug } = params;
    const target = links.collection().totalListings;

    return Request.getRequest<CollectionTotalListingsResponse>({
        ...params,
        query: {
            ...params.query,
            collectionSlug: slug,
        },
        target,
        apiSettingsType: 'daas',
    });
}

export const callGetCollectionTotalListings = (
    params: BaseParams & { slug: string },
): Promise<CollectionTotalListingsResponse> =>
    apiFactory()
        .call(getCollectionTotalListings, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting collection ${params.slug}`;
            throw new Error(errorMessage);
        });

export async function getCollectionTotalOwners(
    params: BaseParams & { slug: string },
): Promise<ApiResult<CollectionTotalOwnersResponse>> {
    const { slug } = params;
    const target = links.collection().totalOwners(slug);

    return Request.getRequest<CollectionTotalOwnersResponse>({
        ...params,
        query: {
            ...params.query,
        },
        target,
        apiSettingsType: 'daas',
    });
}

export const callGetCollectionTotalOwners = (
    params: BaseParams & { slug: string },
): Promise<CollectionTotalOwnersResponse> =>
    apiFactory()
        .call(getCollectionTotalOwners, {
            ...params,
        })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage = result.error.detail ?? `Error getting collection ${params.slug}`;
            throw new Error(errorMessage);
        });

export async function getCollectionStats(
    params: BaseParams & { contractAddress: string[] },
): Promise<ApiResult<CollectionStatsResponse>> {
    return Request.getRequest<CollectionStatsResponse>({
        ...params,
        target: links.daas().graphql().activity().collectionStats(),
        apiSettingsType: 'daasGraphQL',
    });
}

export const callGetCollectionStats = (params: BaseParams) =>
    apiReturnResults<CollectionStatsResponse>(getCollectionStats, params);
